import { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { broadcastApi, tenantsApi } from '@/lib/api';
import {
  Megaphone, Users, Plus, Trash, ArrowsClockwise, PaperPlaneTilt,
  CheckCircle, Clock, Tag, MagnifyingGlass, Gear, ShieldCheck,
  UploadSimple, DownloadSimple, Image, FilePdf, VideoCamera, TextT,
} from '@phosphor-icons/react';

const MEDIA_TYPES = [
  { value: 'text', label: 'Text only', icon: TextT },
  { value: 'image', label: 'Image', icon: Image },
  { value: 'document', label: 'Document / PDF', icon: FilePdf },
  { value: 'video', label: 'Video', icon: VideoCamera },
];

const BROADCAST_DEFAULTS = {
  instance: 'primary',
  batch_size: 50,
  batch_pause_seconds: 60,
  msg_min_delay: 1.0,
  msg_max_delay: 3.0,
  frequency_cap_hours: 0,
  quiet_hours: { enabled: false, start: 21, end: 8 },
};

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-600',
  scheduled: 'bg-blue-100 text-blue-700',
  sending: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export const WhatsAppBroadcast = ({ brandColor = '#002FA7', tenantConfig, setTenantConfig }) => {
  const [view, setView] = useState('campaigns'); // 'campaigns' | 'contacts' | 'settings'

  // ── Settings (broadcast_config) ──
  const [settings, setSettings] = useState({ ...BROADCAST_DEFAULTS });
  const [savingSettings, setSavingSettings] = useState(false);
  useEffect(() => {
    if (tenantConfig?.broadcast_config) {
      setSettings({ ...BROADCAST_DEFAULTS, ...tenantConfig.broadcast_config });
    }
  }, [tenantConfig]);

  // ── Contacts ──
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [contactDialog, setContactDialog] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', tags: '', opt_in: true });
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // ── Campaigns ──
  const [campaigns, setCampaigns] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [draft, setDraft] = useState({ name: '', message_template: 'Hi {name}! ', target_tags: [], scheduled_at: '', media_type: 'text', media_url: '' });
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [sendConfirm, setSendConfirm] = useState(null); // campaign pending send

  const fetchContacts = useCallback(async () => {
    try {
      const { data } = await broadcastApi.listContacts({ search: contactSearch || undefined, page_size: 200 });
      setContacts(data?.data || []);
    } catch (e) { /* silent */ }
  }, [contactSearch]);

  const [broadcastEnabled, setBroadcastEnabled] = useState(true);
  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await broadcastApi.listCampaigns({ page_size: 100 });
      setCampaigns(data?.data || []);
    } catch (e) { /* silent */ }
  }, []);

  const fetchTags = useCallback(async () => {
    try { const { data } = await broadcastApi.tags(); setAllTags(data || []); } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchCampaigns(); fetchTags(); }, [fetchCampaigns, fetchTags]);
  useEffect(() => { if (view === 'contacts') fetchContacts(); }, [view, fetchContacts]);

  // Poll while any campaign is sending.
  useEffect(() => {
    if (!campaigns.some((c) => c.status === 'sending')) return;
    const t = setInterval(fetchCampaigns, 5000);
    return () => clearInterval(t);
  }, [campaigns, fetchCampaigns]);

  // ── Contact actions ──
  const syncCustomers = async () => {
    setSyncing(true);
    try {
      const { data } = await broadcastApi.syncCustomers();
      toast.success(`Synced customers${data?.created != null ? ` (+${data.created} new)` : ''}`);
      fetchContacts();
    } catch (e) { toast.error('Sync failed'); } finally { setSyncing(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) { toast.error('Please choose a .csv file'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await broadcastApi.importContacts(fd);
      const added = data?.created ?? data?.imported ?? data?.added;
      toast.success(`Imported contacts${added != null ? ` (+${added})` : ''}`);
      fetchContacts();
      fetchTags();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleExport = async () => {
    try {
      const res = await broadcastApi.exportContacts();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Export failed'); }
  };

  const saveContact = async () => {
    if (!newContact.name.trim() || !newContact.phone.trim()) { toast.error('Name and phone required'); return; }
    try {
      await broadcastApi.createContact({
        name: newContact.name.trim(),
        phone: newContact.phone.trim(),
        opt_in: newContact.opt_in,
        tags: newContact.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      toast.success('Contact added');
      setContactDialog(false);
      setNewContact({ name: '', phone: '', tags: '', opt_in: true });
      fetchContacts();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add contact'); }
  };

  const deleteContact = async (id) => {
    try { await broadcastApi.deleteContact(id); fetchContacts(); toast.success('Deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const toggleOptIn = async (c) => {
    try { await broadcastApi.updateContact(c.id, { opt_in: !c.opt_in }); fetchContacts(); }
    catch (e) { toast.error('Update failed'); }
  };

  // ── Campaign actions ──
  const optedInCount = contacts.filter((c) => c.opt_in).length;
  const estimateRecipients = () => {
    // Best-effort estimate from loaded contacts (server computes the real set on send).
    if (!contacts.length) return null;
    const eligible = contacts.filter((c) => c.opt_in);
    if (!draft.target_tags.length) return eligible.length;
    return eligible.filter((c) => (c.tags || []).some((t) => draft.target_tags.includes(t))).length;
  };

  const toggleDraftTag = (tag) => {
    setDraft((d) => ({
      ...d,
      target_tags: d.target_tags.includes(tag) ? d.target_tags.filter((t) => t !== tag) : [...d.target_tags, tag],
    }));
  };

  const saveCampaign = async () => {
    if (!draft.name.trim() || !draft.message_template.trim()) { toast.error('Name and message required'); return; }
    setSavingCampaign(true);
    try {
      const mediaType = draft.media_type || 'text';
      if (mediaType !== 'text' && !draft.media_url.trim()) {
        toast.error('Add a media URL or switch to Text only');
        setSavingCampaign(false);
        return;
      }
      await broadcastApi.createCampaign({
        name: draft.name.trim(),
        message_template: draft.message_template,
        media_type: mediaType,
        media_url: mediaType !== 'text' ? draft.media_url.trim() : null,
        target_tags: draft.target_tags,
        scheduled_at: draft.scheduled_at ? new Date(draft.scheduled_at).toISOString() : null,
      });
      toast.success('Campaign saved as draft');
      setCampaignDialog(false);
      setDraft({ name: '', message_template: 'Hi {name}! ', target_tags: [], scheduled_at: '', media_type: 'text', media_url: '' });
      fetchCampaigns();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); } finally { setSavingCampaign(false); }
  };

  const sendCampaign = async (c) => {
    try { await broadcastApi.sendCampaign(c.id); toast.success('Broadcast started'); setSendConfirm(null); fetchCampaigns(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
  };

  const deleteCampaign = async (id) => {
    try { await broadcastApi.deleteCampaign(id); fetchCampaigns(); toast.success('Deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const renderPreview = (tpl) => (tpl || '').replace(/\{name\}/g, 'Priya');

  // ── Settings actions ──
  const saveSettings = async () => {
    if (!tenantConfig?.id) { toast.error('Tenant config not loaded'); return; }
    const clean = {
      instance: settings.instance === 'marketing' ? 'marketing' : 'primary',
      batch_size: Math.max(1, parseInt(settings.batch_size, 10) || 50),
      batch_pause_seconds: Math.max(0, parseFloat(settings.batch_pause_seconds) || 0),
      msg_min_delay: Math.max(0, parseFloat(settings.msg_min_delay) || 0),
      msg_max_delay: Math.max(0, parseFloat(settings.msg_max_delay) || 0),
      frequency_cap_hours: Math.max(0, parseFloat(settings.frequency_cap_hours) || 0),
      quiet_hours: {
        enabled: !!settings.quiet_hours?.enabled,
        start: Math.min(23, Math.max(0, parseInt(settings.quiet_hours?.start, 10) || 0)),
        end: Math.min(23, Math.max(0, parseInt(settings.quiet_hours?.end, 10) || 0)),
      },
    };
    if (clean.msg_max_delay < clean.msg_min_delay) clean.msg_max_delay = clean.msg_min_delay;
    setSavingSettings(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, { broadcast_config: clean });
      if (setTenantConfig && data) setTenantConfig(data);
      setSettings({ ...BROADCAST_DEFAULTS, ...clean });
      toast.success('Broadcast settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally { setSavingSettings(false); }
  };

  return (
    <Card className="p-6 border shadow-sm rounded-lg">
      {/* header */}
      <div className="flex items-center justify-between gap-3 mb-5 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-sm" style={{ backgroundColor: `${brandColor}15` }}>
            <Megaphone size={22} weight="bold" style={{ color: brandColor }} />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 text-base leading-tight">Broadcast</h3>
            <p className="text-xs text-slate-500 mt-0.5">Send to opted-in contacts. Opt-outs &amp; batching are enforced automatically.</p>
          </div>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-md">
          {['campaigns', 'contacts'].map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-sm transition ${view === v ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* ════ CAMPAIGNS ════ */}
      {view === 'campaigns' && (
        <div className="space-y-4">

          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500">{campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}</span>
            <Button onClick={() => setCampaignDialog(true)} style={{ backgroundColor: brandColor }} className="text-white font-bold text-xs">
              <Plus size={15} weight="bold" className="mr-1.5" /> New campaign
            </Button>
          </div>

          {campaigns.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Megaphone size={36} weight="duotone" className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No campaigns yet. Create one to broadcast to your contacts.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {campaigns.map((c) => {
                const pct = c.total_recipients ? Math.round(((c.sent_count + c.failed_count) / c.total_recipients) * 100) : 0;
                return (
                  <div key={c.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800 text-sm truncate">{c.name}</span>
                          <Badge className={`${STATUS_STYLES[c.status] || 'bg-slate-100'} text-[10px] font-bold uppercase px-2 py-0`}>{c.status}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.message_template}</p>
                        {(c.target_tags || []).length > 0 && (
                          <div className="flex gap-1 mt-1.5 flex-wrap">
                            {c.target_tags.map((t) => (
                              <span key={t} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">#{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {(c.status === 'draft' || c.status === 'scheduled') && (
                          <Button size="sm" onClick={() => setSendConfirm(c)} style={{ backgroundColor: brandColor }} className="text-white font-bold h-8 text-xs">
                            <PaperPlaneTilt size={14} weight="bold" className="mr-1" /> Send
                          </Button>
                        )}
                        {c.status !== 'sending' && (
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" onClick={() => deleteCampaign(c.id)}>
                            <Trash size={15} />
                          </Button>
                        )}
                      </div>
                    </div>
                    {(c.status === 'sending' || c.status === 'completed') && (
                      <div className="mt-3">
                        <div className="flex justify-between text-[11px] text-slate-500 mb-1">
                          <span>{c.status === 'sending' && <Clock size={11} weight="bold" className="inline mr-1 animate-pulse" />}{c.sent_count} sent · {c.failed_count} failed · {c.total_recipients} total</span>
                          <span>{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: brandColor }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ════ CONTACTS ════ */}
      {view === 'contacts' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Search contacts…" className="pl-8 text-sm h-9" />
            </div>
            <Button variant="outline" onClick={syncCustomers} disabled={syncing} className="text-xs font-bold h-9">
              <ArrowsClockwise size={14} weight="bold" className={`mr-1.5 ${syncing ? 'animate-spin' : ''}`} /> Sync customers
            </Button>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImport} className="hidden" />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} className="text-xs font-bold h-9">
              <UploadSimple size={14} weight="bold" className="mr-1.5" /> {importing ? 'Importing…' : 'Import CSV'}
            </Button>
            <Button variant="outline" onClick={handleExport} className="text-xs font-bold h-9">
              <DownloadSimple size={14} weight="bold" className="mr-1.5" /> Export
            </Button>
            <Button onClick={() => setContactDialog(true)} style={{ backgroundColor: brandColor }} className="text-white font-bold text-xs h-9">
              <Plus size={15} weight="bold" className="mr-1.5" /> Add
            </Button>
          </div>

          <div className="text-xs text-slate-500">{contacts.length} contacts · <span className="text-green-600 font-semibold">{optedInCount} opted in</span></div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-50">
              {contacts.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No contacts. Use “Sync customers” to import from your customer list.</div>
              ) : contacts.map((c) => (
                <div key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: brandColor }}>
                    {(c.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-400">{c.phone}</div>
                  </div>
                  {(c.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded hidden sm:inline"><Tag size={9} className="inline mr-0.5" />{t}</span>
                  ))}
                  <button onClick={() => toggleOptIn(c)}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.opt_in ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {c.opt_in ? 'opted in' : 'opted out'}
                  </button>
                  <button onClick={() => deleteContact(c.id)} className="text-slate-300 hover:text-red-500"><Trash size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ════ SETTINGS ════ */}
      {view === 'settings' && (
        <div className="space-y-5 max-w-2xl">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
            <ShieldCheck size={18} weight="bold" className="text-blue-500 shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800">
              These controls protect your WhatsApp number from spam bans. Larger batches and shorter delays send faster but raise the risk of being blocked.
            </p>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Sending number</Label>
            <Select value={settings.instance} onValueChange={(v) => setSettings({ ...settings, instance: v })}>
              <SelectTrigger className="mt-1.5 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="primary">Primary support number (same as the bot)</SelectItem>
                <SelectItem value="marketing">Separate marketing number ({'{slug}'}-mkt)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">
              Using a separate number keeps marketing volume off your support line — but it must be paired separately.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Batch size</Label>
              <Input type="number" min={1} value={settings.batch_size}
                onChange={(e) => setSettings({ ...settings, batch_size: e.target.value })} className="mt-1.5 text-sm" />
              <p className="text-[11px] text-slate-400 mt-1">Messages per batch before pausing.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Pause between batches (sec)</Label>
              <Input type="number" min={0} value={settings.batch_pause_seconds}
                onChange={(e) => setSettings({ ...settings, batch_pause_seconds: e.target.value })} className="mt-1.5 text-sm" />
              <p className="text-[11px] text-slate-400 mt-1">Warm-up pause to look human.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Min delay / message (sec)</Label>
              <Input type="number" min={0} step="0.5" value={settings.msg_min_delay}
                onChange={(e) => setSettings({ ...settings, msg_min_delay: e.target.value })} className="mt-1.5 text-sm" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Max delay / message (sec)</Label>
              <Input type="number" min={0} step="0.5" value={settings.msg_max_delay}
                onChange={(e) => setSettings({ ...settings, msg_max_delay: e.target.value })} className="mt-1.5 text-sm" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Frequency cap (hours)</Label>
            <Input type="number" min={0} value={settings.frequency_cap_hours}
              onChange={(e) => setSettings({ ...settings, frequency_cap_hours: e.target.value })} className="mt-1.5 text-sm max-w-[200px]" />
            <p className="text-[11px] text-slate-400 mt-1">Skip contacts messaged within this many hours. <span className="font-semibold">0 = no cap.</span></p>
          </div>

          {/* Quiet hours */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Quiet hours (IST)</Label>
                <p className="text-[11px] text-slate-400 mt-0.5">Campaigns started in this window are deferred to when it ends.</p>
              </div>
              <Switch
                checked={!!settings.quiet_hours?.enabled}
                onCheckedChange={(v) => setSettings({ ...settings, quiet_hours: { ...settings.quiet_hours, enabled: v } })}
              />
            </div>
            {settings.quiet_hours?.enabled && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">From</span>
                <Input type="number" min={0} max={23} value={settings.quiet_hours?.start}
                  onChange={(e) => setSettings({ ...settings, quiet_hours: { ...settings.quiet_hours, start: e.target.value } })} className="text-sm w-20" />
                <span className="text-xs text-slate-500">:00 to</span>
                <Input type="number" min={0} max={23} value={settings.quiet_hours?.end}
                  onChange={(e) => setSettings({ ...settings, quiet_hours: { ...settings.quiet_hours, end: e.target.value } })} className="text-sm w-20" />
                <span className="text-xs text-slate-500">:00</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
            <Button onClick={saveSettings} disabled={savingSettings} style={{ backgroundColor: brandColor }} className="text-white font-bold">
              <Gear size={15} weight="bold" className="mr-1.5" /> {savingSettings ? 'Saving…' : 'Save settings'}
            </Button>
            <Button variant="ghost" className="text-slate-500 text-xs" onClick={() => setSettings({ ...BROADCAST_DEFAULTS })}>
              Reset to defaults
            </Button>
          </div>
        </div>
      )}

      {/* ── New campaign dialog ── */}
      <Dialog open={campaignDialog} onOpenChange={setCampaignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New broadcast campaign</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold">Campaign name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Monsoon service offer" className="mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-xs font-bold">Message <span className="text-slate-400 font-normal">— use {'{name}'} to personalize{draft.media_type !== 'text' ? ', sent as caption' : ''}</span></Label>
              <Textarea value={draft.message_template} onChange={(e) => setDraft({ ...draft, message_template: e.target.value })} rows={4} className="mt-1 text-sm" />
            </div>
            {/* Attachment */}
            <div>
              <Label className="text-xs font-bold">Attachment</Label>
              <div className="flex gap-1.5 mt-1.5">
                {MEDIA_TYPES.map((m) => {
                  const Ico = m.icon;
                  const active = draft.media_type === m.value;
                  return (
                    <button key={m.value} type="button" onClick={() => setDraft({ ...draft, media_type: m.value })}
                      className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition ${active ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200'}`}
                      style={active ? { backgroundColor: brandColor } : {}}>
                      <Ico size={14} weight="bold" /> {m.label}
                    </button>
                  );
                })}
              </div>
              {draft.media_type !== 'text' && (
                <Input value={draft.media_url} onChange={(e) => setDraft({ ...draft, media_url: e.target.value })}
                  placeholder={`Public ${draft.media_type} URL (https://…)`} className="mt-2 text-sm" />
              )}
            </div>
            {/* Live preview */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-slate-400 mb-1.5">Preview</div>
              <div className="bg-[#dcf8c6] inline-block max-w-[85%] px-3 py-2 rounded-2xl rounded-bl-sm text-sm whitespace-pre-wrap shadow-sm">
                {draft.media_type !== 'text' && (
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold text-slate-500 bg-white/60 rounded px-2 py-1">
                    {(() => { const M = MEDIA_TYPES.find((x) => x.value === draft.media_type); const I = M?.icon || TextT; return <I size={13} weight="bold" />; })()}
                    {draft.media_url ? draft.media_url.split('/').pop() : `${draft.media_type} attachment`}
                  </div>
                )}
                {renderPreview(draft.message_template)}
              </div>
            </div>
            {allTags.length > 0 && (
              <div>
                <Label className="text-xs font-bold">Target tags <span className="text-slate-400 font-normal">— empty = all opted-in</span></Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {allTags.map((t) => (
                    <button key={t} onClick={() => toggleDraftTag(t)}
                      className={`text-xs px-2.5 py-1 rounded-full font-semibold transition ${draft.target_tags.includes(t) ? 'text-white' : 'bg-slate-100 text-slate-500'}`}
                      style={draft.target_tags.includes(t) ? { backgroundColor: brandColor } : {}}>
                      #{t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold">Schedule <span className="text-slate-400 font-normal">— optional</span></Label>
              <Input type="datetime-local" value={draft.scheduled_at} onChange={(e) => setDraft({ ...draft, scheduled_at: e.target.value })} className="mt-1 text-sm" />
            </div>
            {estimateRecipients() != null && (
              <p className="text-xs text-slate-500">≈ <span className="font-bold" style={{ color: brandColor }}>{estimateRecipients()}</span> recipients (based on loaded contacts).</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampaignDialog(false)}>Cancel</Button>
            <Button onClick={saveCampaign} disabled={savingCampaign} style={{ backgroundColor: brandColor }} className="text-white font-bold">Save draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add contact dialog ── */}
      <Dialog open={contactDialog} onOpenChange={setContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Name</Label>
              <Input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} className="mt-1 text-sm" /></div>
            <div><Label className="text-xs font-bold">Phone (E.164, e.g. +9199…)</Label>
              <Input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className="mt-1 text-sm" /></div>
            <div><Label className="text-xs font-bold">Tags (comma separated)</Label>
              <Input value={newContact.tags} onChange={(e) => setNewContact({ ...newContact, tags: e.target.value })} placeholder="vip, mumbai" className="mt-1 text-sm" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={newContact.opt_in} onCheckedChange={(v) => setNewContact({ ...newContact, opt_in: v })} />
              <Label className="text-xs">Opted in to broadcasts</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialog(false)}>Cancel</Button>
            <Button onClick={saveContact} style={{ backgroundColor: brandColor }} className="text-white font-bold">Add contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Send confirm dialog ── */}
      <Dialog open={!!sendConfirm} onOpenChange={(o) => !o && setSendConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Send this broadcast?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            “{sendConfirm?.name}” will be sent to all opted-in contacts{(sendConfirm?.target_tags || []).length ? ` tagged ${sendConfirm.target_tags.map((t) => `#${t}`).join(', ')}` : ''}.
            Messages go out in batches with delays to protect your number.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendConfirm(null)}>Cancel</Button>
            <Button onClick={() => sendCampaign(sendConfirm)} style={{ backgroundColor: brandColor }} className="text-white font-bold">
              <PaperPlaneTilt size={15} weight="bold" className="mr-1.5" /> Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
